// What a routine's status pill says, and why.
//
// The whole point of the Routines redesign is that a routine states its condition on the card, so nobody
// has to open it to learn why nothing is happening. That makes this the most important pure function on
// the screen: it turns four independent booleans on the entity into ONE thing the pill can say.
//
// Precedence matters and is deliberate — a routine can be both `paused` and `pausedOutOfCredits`, and the
// credits case must win, because "you ran out of credits" is actionable and "paused" is not. Likewise a
// trigger-only routine is reported as trigger-driven rather than as "active", because "Active" next to no
// schedule reads as a bug to anyone who then goes looking for the next run.
//
// Mirrored 1:1 by `RoutineStatus.swift` and `RoutineStatus.kt`; all three unit-test the same table.

import type { TokenBackedBadgeVariant } from './task-status';

/** A routine, as far as status is concerned. */
export interface StatusableRoutine {
  paused?: boolean;
  pausedOutOfCredits?: boolean;
  /** Set by the server: the routine self-fires and is live (cron or triggers, and not paused). */
  scheduled?: boolean;
  /** Set by the server: the routine has at least one event trigger. */
  hasTriggers?: boolean;
  cron?: string;
}

export type RoutineStatus = 'active' | 'trigger' | 'paused' | 'out-of-credits' | 'no-schedule';

/** The pill's Style and its catalog key, per status. */
export interface RoutineStatusBadge {
  status: RoutineStatus;
  labelKey: string;
  variant: TokenBackedBadgeVariant;
}

const BADGES: Record<RoutineStatus, RoutineStatusBadge> = {
  active: { status: 'active', labelKey: 'routines.status.active', variant: 'done' },
  trigger: { status: 'trigger', labelKey: 'routines.status.trigger', variant: 'in-progress' },
  paused: { status: 'paused', labelKey: 'routines.status.paused', variant: 'paused' },
  'out-of-credits': { status: 'out-of-credits', labelKey: 'routines.status.outOfCredits', variant: 'waiting-on-you' },
  'no-schedule': { status: 'no-schedule', labelKey: 'routines.status.noSchedule', variant: 'waiting-on-a-reply' },
};

/**
 * Resolve a routine's single status.
 *
 * Order is the contract:
 *  1. **out of credits** — the account stopped it, and topping up is the fix. Beats plain `paused`
 *     because the entity sets both, and the generic word hides the actionable reason.
 *  2. **paused** — the user stopped it. The toggle turns it back on.
 *  3. **trigger** — no cron, but at least one event trigger: it fires on an event, so there is no
 *     next-run time to show and "Active" would send the reader looking for one.
 *  4. **active** — a live cron.
 *  5. **no schedule** — neither cron nor trigger. Reachable: an LLM-authored routine whose cron never
 *     got written. It is not paused and it is not going to run, and the pill has to say so rather than
 *     letting it sit among the active ones.
 */
export function routineStatus(routine: StatusableRoutine): RoutineStatus {
  if (routine.pausedOutOfCredits) return 'out-of-credits';
  if (routine.paused) return 'paused';
  const hasCron = typeof routine.cron === 'string' && routine.cron.trim().length > 0;
  if (!hasCron) return routine.hasTriggers ? 'trigger' : 'no-schedule';
  return 'active';
}

/** The pill to render for a routine. */
export function routineStatusBadge(routine: StatusableRoutine): RoutineStatusBadge {
  return BADGES[routineStatus(routine)];
}

/** Whether the on/off toggle should read as on. Only a live routine does. */
export function routineIsOn(routine: StatusableRoutine): boolean {
  return !routine.paused && !routine.pausedOutOfCredits;
}

/**
 * Whether the user can work the toggle. A routine the *account* paused cannot be resumed from here —
 * flipping it would write `paused: false` and the next credit check would put it straight back, so the
 * control would lie. Buying credits is the way out, which the pill already names.
 */
export function routineToggleEnabled(routine: StatusableRoutine): boolean {
  return !routine.pausedOutOfCredits;
}
